<script lang="ts">
	import { DataHandlerUserAdmin, UserPriv, type User } from "luxedo-data"
	import { openConfirmOverlay, openTextOverlay } from "overlay"
	import { Toast } from "toaster"
	import { LuxedoRPC } from "luxedo-rpc"
	import { EditIcon, TrashIcon } from "luxedo-icons"
	import { Reactivity } from "luxedo-reactivity"

	interface Props {
		user: User
	}

	let { user }: Props = $props()
	$effect.pre(() => {
		Reactivity.useProps(user, ["privs"])
	})
	let privs = $derived(Reactivity.propOf(user, "privs"))

	async function updateServerPrivs(privList: Array<string>) {
		try {
			await LuxedoRPC.api.admin.set_user_privileges(user.id, privList)
			setTimeout(async () => {
				await DataHandlerUserAdmin.pull()
				Toast.success("Privileges updated.")
			}, 1500)
		} catch (e) {
			console.error("ERROR while trying to update privileges.", { error: e })
			Toast.error("Unable to update privilege. Please try again.")
		}
	}

	function getPrivList(initPriv?: string, newPriv?: UserPriv) {
		const privList = [...user.privs]
		if (initPriv) {
			const privIndex = privList.findIndex((p) => p === initPriv)
			privList[privIndex] = newPriv
		}
		return privList
	}

	function getRemovedPrivList(removePriv: UserPriv) {
		const privList = [...user.privs]
		const privIndex = privList.indexOf(removePriv)
		if (privIndex > -1) {
			privList.splice(privIndex, 1)
			return privList
		}
		return privList
	}

	function editPrivilege(priv?: string) {
		const initPriv = priv
		openTextOverlay(
			{
				defaultValue: priv,
				label: "Privilege",
				onSubmit: (newPriv) => {
					const privList = getPrivList(initPriv, newPriv as UserPriv)
					updateServerPrivs(privList)
				},
			},
			{
				heading: "Update Privilege",
			}
		)
	}

	function createPrivilege() {
		openTextOverlay(
			{
				label: "Privilege",
				onSubmit: (newPriv) => {
					const privList = [...user.privs, newPriv]
					updateServerPrivs(privList)
				},
			},
			{
				heading: "New Privilege",
			}
		)
	}

	function removePrivilege(priv) {
		const initPriv = priv
		openConfirmOverlay(
			{
				prompt: "Are you sure you want to delete this privilege? ",
				invertWarning: true,
				buttons: {
					confirm: {
						text: "Yes",
						onClick: () => {
							const privList = getRemovedPrivList(initPriv)
							updateServerPrivs(privList)
						},
					},
				},
			},
			{
				heading: "Remove Privilege",
			}
		)
	}
</script>

<div class="flex-column center-hor center-ver">
	{#each privs ?? [] as priv}
		<span class="priv-row">
			{priv}
			<div class="button-container">
				<button
					class="small cancel only-icon"
					title="Remove Privilege"
					onclick={() => removePrivilege(priv)}
				>
					<TrashIcon />
				</button>
				<button class="small only-icon" title="Edit Privilege" onclick={() => editPrivilege(priv)}>
					<EditIcon />
				</button>
			</div>
		</span>
	{/each}
	<button class="small" id="add-priv-btn" onclick={createPrivilege}>Add Privilege</button>
</div>

<style>
	.priv-row {
		margin: 0.1rem 0;
		width: 12rem;
		font-size: var(--text-small);
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		border-radius: var(--br);
		padding: 0.2rem 0 0.2rem 0.5rem;
	}

	.priv-row:nth-of-type(odd) {
		background-color: var(--color-main);
	}

	#add-priv-btn {
		margin: 0.5rem 0 0 0;
	}

	.button-container {
		width: fit-content;
	}

	.button-container button {
		margin: 0 0.25rem;
		padding: 0.25rem;
	}

	.button-container button :global(svg) {
		max-height: 1rem;
		max-width: 1rem;
		height: 1rem;
		width: auto;
	}
</style>
